import { IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import styles from "./modals.module.scss";
import { BaseModal } from "@whatslly/ui-components/modals/BaseModal";
import React, { useMemo, useRef, useState } from "react";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { useTranslation } from "react-i18next";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { useDependencyInjector } from "@whatslly/commons/di";
import { IApiKeysStore } from "../IApiKeysStore";
import { TextInputWithCopy } from "@whatslly/ui-components/v2/TextInputWithCopy";

type FormDataType = { apiKeyName: string };

export const CreateApiKeyModal: React.FC<IModalInstance> = ({ closeModal }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const store = useDependencyInjector<IApiKeysStore>(IApiKeysStore);
  const formikContext = useRef<FormikProps<FormDataType>>(null);
  const validate = async (values: FormDataType) => {
    const errors: FormikErrors<PartialRecord<keyof FormDataType, string>> = {};
    if (!values.apiKeyName) {
      errors.apiKeyName = t("field_is_required");
    }
    return errors;
  };

  const onSubmit = async (data: FormDataType) => {
    setLoading(true);
    const res = await store.createApiKey({ apiKeyName: data.apiKeyName, t });
    if (res) {
      store.keyPair = res;
    }
  };

  const content = useMemo(() => {
    if (store.keyPair?.apiKey) {
      return (
        <>
          <div data-testid="api_key_desc" className={styles.desc}>
            {t("api_keys_settings_page.create_api_key_desc")}
          </div>
          <div className={styles.field}>
            <div className={styles.filedLabel}>{t("api_keys_settings_page.api_key")}</div>
            <div className={styles.inputCopy}>
              <TextInputWithCopy data-testid={"api_key"} value={store.keyPair.apiKey} disabled={true} />
            </div>
          </div>
          <div className={styles.field}>
            <div className={styles.filedLabel}>{t("api_keys_settings_page.api_secret")}</div>
            <div className={styles.inputCopy}>
              <TextInputWithCopy data-testid={"api_secret"} value={store.keyPair.apiSecret} disabled={true} />
            </div>
          </div>
          <ModalButtons
            submitText={t("api_keys_settings_page.close")}
            testId={"api-key-ok-button"}
            onSubmit={() => {
              store.refresh();
              closeModal();
            }}
            hideCancel={true}
          />
        </>
      );
    }
    return (
      <FormElement initialValues={{ apiKeyName: "" }} validate={validate} onSubmit={onSubmit} formikRef={formikContext}>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("api_keys_settings_page.api_key_name")} />
          <FormTextInput name={"apiKeyName"} data-testid="api_key_name" paddingInline="none" />
        </div>
        <ModalButtons
          submitText={t("destinations_page.save")}
          testId={"api-key-save-button"}
          hideCancel={true}
          isSubmitting={loading}
          isSubmitDisabled={loading}
        />
      </FormElement>
    );
  }, [store.keyPair, loading]);

  return (
    <BaseModal
      header={<ModalHeader closeModal={closeModal}>Create Api Key</ModalHeader>}
      closeModal={closeModal}
      className={styles.modal}
      bodyClassName={styles.body}
      headerClassName={styles.header}
      closeOnClickOutside={false}
      closeOnEscape={false}
      testId="create-api-key-modal"
    >
      {content}
    </BaseModal>
  );
};
